import { FC, KeyboardEvent, useState } from 'react';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import { ArrowRightIcon, NavCloseIcon } from 'assets/icons';
import { ErrorScreenWithRetry, Loader, NoPermissions, SidePanel, Tabs } from 'components';
import {
  selectCurrentTeam,
  selectCurrentTeamInvoicePermissions,
  selectCurrentTeamRefundPermissions
} from 'containers/app/appSlice';
import { useAppSelector } from 'hooks';
import routesPath from 'routes/RoutesPath';
import { SidePanelRouteState } from 'routes/types';
import { OutletContextType } from 'types/baseTypes';
import { logAnalyticEvent } from 'utils/analytics';
import { useGetDisputeVisibilityQuery } from '../api';
import { CleverTapEventsDisputes } from '../events';
import { DisputeTechnicianRow, DisputeVisibility } from './disputeVisibility.model';
import { DisputeChevron, DisputeZoneChip } from './components/DisputeZoneChip';
import {
  buildAllDisputesPreset,
  buildReceiptsListPreset,
  normalizeDisputeVisibility,
  resolveDisputeWindowRange
} from './utils';

const WINDOW_TABS = [
  { id: 30, title: '30 days' },
  { id: 90, title: '90 days' }
];

const isForbidden = (error: unknown) => {
  if (!error || typeof error !== 'object') return false;
  const status = 'status' in error ? error.status : undefined;
  const data = 'data' in error ? (error.data as { code?: number } | undefined) : undefined;
  return status === 403 || data?.code === 403;
};

const PANEL_CLASS_NAME = 'flex h-full w-96 flex-col';

const DisputeTechnicians: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { handleSidePanelClose } = useOutletContext<OutletContextType>();
  const currentTeam = useAppSelector(selectCurrentTeam);
  const { viewDisputes } = useAppSelector(selectCurrentTeamRefundPermissions);
  const invoicePermissions = useAppSelector(selectCurrentTeamInvoicePermissions);
  const [selectedWindow, setSelectedWindow] = useState('30 days');
  const canView =
    Boolean(viewDisputes) &&
    (Boolean(invoicePermissions.viewReceiptsBySelf) || Boolean(invoicePermissions.viewReceiptsByTeam));

  const { data, isLoading, isError, error, refetch } = useGetDisputeVisibilityQuery(currentTeam?.id ?? 0, {
    skip: !currentTeam?.id || !canView
  });
  const summary = data ? normalizeDisputeVisibility(data) : null;

  const isRootState = !(location.state as SidePanelRouteState | null)?.fromSidePanel;

  const handleBack = () => {
    if (isRootState) {
      handleSidePanelClose();
      return;
    }
    navigate(-1);
  };

  const handleWindowSelect = (tab: string) => {
    const days = tab === '90 days' ? 90 : 30;
    logAnalyticEvent(CleverTapEventsDisputes.webDisputesByTechnician, { action: 'window', window: days });
    setSelectedWindow(tab);
  };

  if (!canView || isForbidden(error)) {
    return (
      <SidePanel isOpen={true} onClose={handleSidePanelClose} shouldAnimate={location.state?.shouldAnimate}>
        <div className={PANEL_CLASS_NAME}>
          <TechniciansHeader onBack={handleBack} isRoot={isRootState} />
          <NoPermissions handleErrorBack={handleBack} />
        </div>
      </SidePanel>
    );
  }

  return (
    <SidePanel isOpen={true} onClose={handleSidePanelClose} shouldAnimate={location.state?.shouldAnimate}>
      <div className={PANEL_CLASS_NAME}>
        <TechniciansHeader onBack={handleBack} isRoot={isRootState} />
        <div className="border-b border-borderGray px-4">
          <Tabs
            tabs={WINDOW_TABS}
            selectedTab={selectedWindow}
            onTabSelect={handleWindowSelect}
            tabStyle="!min-w-0 px-2.5 !text-sbase"
            tabBlurredState="!font-normal opacity-100"
          />
        </div>
        <div className="customNormalScroll min-h-0 flex-1 overflow-auto">
          {isLoading || !currentTeam?.id ? <Loader /> : null}
          {!isLoading && (isError || !summary) ? <ErrorScreenWithRetry handleReload={refetch} /> : null}
          {summary ? <TechnicianList summary={summary} selectedWindow={selectedWindow} /> : null}
        </div>
      </div>
    </SidePanel>
  );
};

const TechniciansHeader: FC<{ onBack: () => void; isRoot: boolean }> = ({ onBack, isRoot }) => {
  const handleBackKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    onBack();
  };

  return (
    <div className="flex w-full items-center gap-2 pt-5 pl-[14px] pb-4">
      <div
        onClick={onBack}
        onKeyDown={handleBackKeyDown}
        className="cursor-pointer"
        role="button"
        tabIndex={0}
        aria-label={isRoot ? 'Close' : 'Go back'}>
        {isRoot ? <NavCloseIcon id="dispute-technicians-close" /> : <ArrowRightIcon className="rotate-180" />}
      </div>
      <div className="text-17px font-semibold text-heading">Disputes by technician</div>
    </div>
  );
};

const TechnicianList: FC<{ summary: DisputeVisibility; selectedWindow: string }> = ({ summary, selectedWindow }) => {
  const navigate = useNavigate();
  const days = selectedWindow === '90 days' ? 90 : 30;
  const rows = days === 90 ? summary.technicians90 : summary.technicians30;
  const disputeWindow = summary.windows.find(item => item.days === days);
  const range = resolveDisputeWindowRange(days, disputeWindow?.windowStart, disputeWindow?.windowEnd);

  if (!rows.length) {
    return (
      <div className="flex h-full items-center justify-center px-6 text-sbase text-accentText">
        No technician payments in this window.
      </div>
    );
  }

  const handleOpenDisputes = (row: DisputeTechnicianRow) => {
    logAnalyticEvent(CleverTapEventsDisputes.webDisputesByTechnician, { action: 'disputed', window: days });
    navigate(routesPath.DISPUTES, {
      state: { disputeListPreset: buildAllDisputesPreset(range.start, range.end, row.merchantId) }
    });
  };

  const handleOpenPayments = (row: DisputeTechnicianRow) => {
    logAnalyticEvent(CleverTapEventsDisputes.webDisputesByTechnician, { action: 'payments', window: days });
    navigate(routesPath.RECEIPTS, {
      state: {
        showSidePanel: false,
        receiptsListPreset: buildReceiptsListPreset(range.start, range.end, row.merchantId)
      }
    });
  };

  return (
    <ul className="flex flex-col gap-3 p-4">
      {rows.map(row => (
        <li key={row.merchantId} className="rounded-md border border-secondaryBtn px-4 py-3.5">
          <div className="flex items-center gap-2.5">
            <span className="flex-1 truncate text-sbase font-semibold text-heading">{row.name}</span>
            <DisputeZoneChip text={row.chip} zone={row.zone} />
          </div>
          <div className="mt-2 flex items-center justify-between gap-3">
            <button
              type="button"
              className="flex min-w-0 items-center gap-1.5 text-left text-px13 font-semibold text-secondary"
              onClick={() => handleOpenDisputes(row)}
              aria-label={`Disputed ${row.disputed} for ${row.name}`}>
              <span className="truncate">Disputed {row.disputed}</span>
              <DisputeChevron />
            </button>
            <button
              type="button"
              className="flex min-w-0 items-center gap-1.5 text-left text-px13 font-semibold text-secondary"
              onClick={() => handleOpenPayments(row)}
              aria-label={`Payments ${row.payments} for ${row.name}`}>
              <span className="truncate">Payments {row.payments}</span>
              <DisputeChevron />
            </button>
          </div>
        </li>
      ))}
    </ul>
  );
};

export default DisputeTechnicians;
